import { AuthViewProps } from "@homebound/auth-components";
import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signInPath } from "src/routes/routesDef";

// Key for storing redirect URL in localStorage
export const REDIRECT_KEY = "authRedirectUrl";

export type AuthProps = AuthViewProps & { isAuthenticating: boolean };

/**
 * Normally we would use `AuthenticatedRoute` and `UnauthenticatedRoute` from `"@homebound/auth-components`,
 * however that package assumes react-router v5, and this repo is using v6 which is not compatible (dropped
 * support for render functions).
 */
export function AuthWrapper(props: { children: ReactNode; requireAuth: boolean; authProps: AuthProps }) {
  const { children, requireAuth, authProps } = props;
  const navigate = useNavigate();

  const { isAuthenticating, isAuthenticated } = authProps;

  // Redirect based on auth status, in the future if we need unauthenticated pages that don't redirect we can revisit
  useEffect(() => {
    // If we're currently checking the auth status, wait until that resolves before redirecting
    if (isAuthenticating) return;

    if (requireAuth && !isAuthenticated) {
      // Preserve current URL for post-auth navigation
      const currentPath = window.location.pathname + window.location.search;
      localStorage.setItem(REDIRECT_KEY, currentPath);
      navigate(signInPath);
    } else if (!requireAuth && isAuthenticated) {
      const storedRedirect = localStorage.getItem(REDIRECT_KEY);
      localStorage.removeItem(REDIRECT_KEY); // Clean up
      navigate(storedRedirect ?? "/"); // Navigate to stored redirect or default to landing page
    }
  }, [requireAuth, isAuthenticated, isAuthenticating, navigate]);

  return <>{children}</>;
}
